import { useEffect, useReducer, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import snakeCaseKeys from 'snakecase-keys';
import { message } from 'antd';
import { t } from 'ttag';

import { Meta } from '../types/Response';

import { fromQuery, toQuery, compareQuery, mergeQueries, Query } from './query-service';

// max rows per page
export const PAGE_SIZE = 20;

type State<TData = any> = Query & {
  data?: TData[];
  loading: boolean;
  filterKeys: any[];
  total: number;
  page: number;
};

const initialState: State = {
  total: -1,
  page: 1,
  data: [],
  loading: false,
  limit: PAGE_SIZE,
  search: '',
  filter: {},
  filterKeys: [],
  sort: { field: '', order: '' },
};

enum Actions {
  RequestPending,
  RequestSuccess,
  SetPage,
  SetFilter,
  SetSort,
  SetSearch,
  Reset,
  RequestError,
}

type Payload<T> = {
  data?: T[];
  order?: string;
  field?: string;
  page?: number;
} & Pick<Meta, 'pagination'>;

const format = <T>(response: Payload<T>) => {
  const { data, pagination: source } = response;

  const pagination = {
    total: source?.totalRecordCount ?? source?.totalItemsCount ?? -1,
    ...source,
  };

  return { data, pagination };
};

const reducer = <T>(state: any, action: { type: Actions; payload?: Payload<T> }) => {
  switch (action.type) {
    case Actions.RequestPending: {
      return { ...state, loading: true };
    }
    case Actions.RequestError: {
      return { ...state, loading: false };
    }
    case Actions.RequestSuccess: {
      const {
        pagination: { total },
        data,
      } = format(action.payload);

      return { ...state, loading: false, data, total };
    }
    case Actions.SetPage: {
      return { ...state, page: action.payload.page };
    }
    case Actions.SetFilter: {
      const { payload } = action;
      const [keys, values] = [Object.keys(payload), Object.values(payload)];

      if (values.length === 0) {
        return {
          ...state,
          filter: {},
          filterKeys: [...keys],
        };
      }

      const nextState = {
        ...state,
        filter: keys
          .filter(key => payload[key]?.length > 0)
          .reduce((prev, key) => ({ ...prev, [key]: payload[key] }), {}),
        filterKeys: Array.from(new Set([...state.filterKeys, ...keys])),
      };

      const prevFilter = Object.values(state.filter);

      if (prevFilter.length > 0) {
        // if there was a filter applied before then let the page and total be the same
        // as it was before so that it would allow to change pages with filters being
        // applied
        return { ...nextState };
      }
      // if a filter is applied for the first time, then reset the value of page and total
      const { page, total } = initialState;
      return {
        ...nextState,
        page,
        total,
      };
    }
    case Actions.SetSort: {
      return { ...state, sort: action.payload };
    }
    case Actions.SetSearch: {
      const { page, total } = initialState;
      return { ...state, page, total, search: action.payload };
    }
    case Actions.Reset: {
      return { ...state, ...initialState };
    }
    default:
      return { ...state };
  }
};

// extract payload required to build query with provided state
const extractPayload = <T>(state: State<T>) => {
  const { search, filter, sort, page, limit } = state;
  const offset = (page - 1) * Number(limit);

  const payload = {
    search,
    filter,
    sort,
    limit,
    offset,
  };

  return payload;
};

// history should only be updated if table has invoked `onChange` callback
const shouldUpdateHistory = payload =>
  ['offset', 'search', 'filter', 'sort'].reduce((prev, next) => prev || payload[next], false);

/**
 * useTable is a hook that facilitates any table based component to have
 * pagination, sorting, filtering and searching by taking an API function and
 * page size (which is set to 20 by default)
 *
 * @param {function} `fn` _must_ be a stable reference; i.e. a top-level function or
 * `useCallback`. It will be called every time the reference changes.
 * @param {object} `pageSettings` TODO: Document what this does and what it accepts
 *
 * @example
 * ```
 * const fn = useCallback((some, args) => anotherFn(eventId, some, args), [eventId]);
 * const props = useTable(fn);
 * -- snip --
 * <Table {...props} />
 * ```
 *
 * TODO: make this a return param
 *
 * it returns the following:
 * - `pagination`: an object contains configuration specifically Antd's Pagination component
 *   - `total`: total number of records available
 *   - `pageSize`: page size that is being use to set the `offset`
 *   - `current`: current page (Note: it is `current` instead of `page` because that's how Ant expects)
 *   - `onChange`: a callback that changes the value of current page to update the data
 * - `dataSource`: data coming from data intended for Antd's Table component
 * - `loading`: set to `true` when an API call is in progress
 * - `onChange`: should be directly attached to Table component for searching and sorting
 * - `onSearch`: callback to set search and invoke API call to fetch data based on provided keywords
 * - `reset`: resets everything back to default
 * - `forceReload`: a callback function to reload data from API using the previous settings
 *                    it is mostly to load updated data after performing any action (add/update/delete)
 *
 * Note: the returned object is formatted in a way it could be directly passed to Antd's table like:
 *
 * @deprecated For any new code, please switch to `useTableQuery` instead.
 */
const useTable = <T>(fn: (payload: any, query: string) => APIResponse<T[]>, pageSettings = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryState = fromQuery(location.search);

  const [state, dispatch] = useReducer<
    React.Reducer<State<T>, { type: Actions; payload?: Payload<T> }>
  >(reducer, {
    ...initialState,
    ...pageSettings,
    ...queryState,
    ...(queryState?.filter ? { filterKeys: Object.keys(queryState.filter) } : {}),
    page: queryState.offset
      ? Number(queryState.offset) / Number(queryState.limit) + 1
      : initialState.page,
  });

  if (typeof fn !== 'function') {
    throw new Error('fn needs to be a promise based function');
  }

  const onChange = useCallback(
    (pagination?: any, filter?: any, sort?: any) => {
      if (filter || Object.keys(filter ?? {}).length > 0) {
        dispatch({ type: Actions.SetFilter, payload: { ...state.filter, ...filter } });
      }

      if (sort && (sort?.order !== state.sort.order || sort?.field !== state.sort.field)) {
        const { order, field } = sort;

        dispatch({ type: Actions.SetSort, payload: { order, field } });
      }

      if (pagination && pagination?.current !== state.page) {
        dispatch({ type: Actions.SetPage, payload: { page: pagination.current } });
      }
    },
    [state.filter, state.page, state.sort.field, state.sort.order],
  );

  const syncQuery = useCallback(
    query => {
      const prev = toQuery(extractPayload(state));

      if (compareQuery(query, prev)) {
        return;
      }

      const next = mergeQueries(query, prev);
      navigate({ ...location, search: next.toString() }, { replace: true });
    },
    [state, navigate, location],
  );

  const onSearch = useCallback(value => {
    dispatch({ type: Actions.SetSearch, payload: value });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: Actions.Reset });
  }, []);

  const makeRequest = useCallback(
    (force = false) => {
      dispatch({ type: Actions.RequestPending });

      const payload = extractPayload(state);
      const query = toQuery(payload);

      // the sort value will be camel case throught
      // except when sending to API, we convert that to snake case
      // the below code does exactly that
      const queryParams = new URLSearchParams(query);
      const sortBy = queryParams.get('sort');
      if (sortBy) {
        // the custom fields sort values are in the format cf[3347] for ascending and -cf[3347] for descending
        // the snakeCaseKeys convert them to cf_3347 in both ascending and descending case
        // the exclude regex tells the snakeCaseKeys to ignore for the custom field sort values
        const keys = snakeCaseKeys({ [sortBy]: '' }, { exclude: [/^-?cf\[\d+\]$/] });
        const sortValue = Object.keys(keys)[0];
        queryParams.set('sort', payload.sort.order === 'descend' ? `-${sortValue}` : sortValue);
      }

      fn(payload, `?${queryParams.toString()}`)
        .then(response => {
          const {
            data,
            meta: { pagination },
          } = response;

          if (data?.length === 0 && payload?.offset > 0) {
            // if data array is empty and offset is greater than zero then
            // it means all contents of the page has been deleted and when
            // that happens then it should go back to previous page.
            dispatch({ type: Actions.SetPage, payload: { page: state.page - 1 } });

            // early return to not execute then rest as it doesn't matter
            return;
          }

          dispatch({
            type: Actions.RequestSuccess,
            payload: {
              pagination,
              data,
            },
          });

          // only update the history when it's not a _forced_ request
          // as forced request would use the same params as previous
          // one so there's no point of creating a duplicate history
          // item. Furthermore, history should only be updated if table
          // has invoked `onChange` callback by changing either one of
          // these properties: 'offset', 'search', 'filter', 'sort'.
          if (force || !shouldUpdateHistory(payload) || compareQuery(location.search, query)) {
            return;
          }

          const currentParams = new URLSearchParams(location.search);
          const tableParams = new URLSearchParams(query);

          // check if it has related entries in the current params
          const relatedEntries = Array.from(currentParams.entries()).filter(([key]) =>
            ['offset', 'search', 'limit', 'sort', ...state.filterKeys].includes(key),
          );

          if (relatedEntries.length > 0) {
            // remove related entries from current params
            relatedEntries.forEach(([key]) => currentParams.delete(key));

            // add new table params to current params
            Array.from(tableParams.entries()).forEach(([key, value]) =>
              currentParams.set(key, value),
            );

            // sort the params alphabetically
            currentParams.sort();

            navigate({ ...location, search: currentParams.toString() }, { replace: true });
          }
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error(err);
          dispatch({ type: Actions.RequestError });
          message.error(t`An unknown error occurred`);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fn, state.page, state.filter, state.sort, state.search],
  );

  const forceReload = useCallback(() => makeRequest(true), [makeRequest]);

  useEffect(() => makeRequest(), [makeRequest]);

  return {
    pagination: {
      total: state.total,
      pageSize: Number(state.limit) ?? PAGE_SIZE,
      current: state.page,
    },
    dataSource: state.data,
    loading: state.loading,
    onChange,
    onSearch,
    reset,
    syncQuery,
    forceReload,
  };
};

export default useTable;
